import React, { useState, useEffect } from 'react';
import styles from '../style/ThresholdModal.module.css';
import WarningModal from './WarningModal';

const EditIcon = () => <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>;
const CheckIcon = () => <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>;
const CancelIcon = () => <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>;

function ThresholdModal({ isOpen, onClose, onSave, activeFile, sensorColumns, savedThresholds, sensorTranslations, onRenameSensor }) {
    const [activeTab, setActiveTab] = useState('percentage');
    const [pctThresholds, setPctThresholds] = useState({});
    const [lowHighThresholds, setLowHighThresholds] = useState({});
    const [isWarningOpen, setWarningOpen] = useState(false);
    const [editingSensor, setEditingSensor] = useState(null);
    const [tempName, setTempName] = useState('');

    const initializeDefaults = () => {
        const initialPct = {};
        const initialLowHigh = {};

        const savedPct = savedThresholds?.percentage || {};
        const savedLowHigh = savedThresholds?.lowhigh || {};

        sensorColumns.forEach(sensorObj => {
            const sensorName = sensorObj.name;
            initialPct[sensorName] = savedPct[sensorName] ?? 25.0;
            initialLowHigh[sensorName] = savedLowHigh[sensorName] ?? { low: 0, high: 100 };
        });
        setPctThresholds(initialPct);
        setLowHighThresholds(initialLowHigh);
    };

    useEffect(() => {
        if (isOpen) {
            initializeDefaults();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, sensorColumns, savedThresholds]);

    if (!isOpen) return null;

    const handlePctChange = (sensorName, value) => {
        setPctThresholds(prev => ({ ...prev, [sensorName]: value === '' ? '' : Number(value) }));
    };

    const handleLowHighChange = (sensorName, key, value) => {
        setLowHighThresholds(prev => ({
            ...prev,
            [sensorName]: { ...prev[sensorName], [key]: value === '' ? '' : Number(value) }
        }));
    };

    const startEditing = (sensorId, currentDisplayName) => {
        setEditingSensor(sensorId);
        setTempName(currentDisplayName === sensorId ? '' : currentDisplayName);
    };

    const cancelEditing = () => {
        setEditingSensor(null);
        setTempName('');
    };

    const saveName = (sensorId) => {
        if (onRenameSensor) {
            onRenameSensor(sensorId, tempName);
        }
        setEditingSensor(null);
    };

    const handleSave = () => {
        const analysisType = activeTab;
        const thresholdsToSave = analysisType === 'percentage' ? pctThresholds : lowHighThresholds;
        onSave(analysisType, thresholdsToSave);
        onClose();
    };

    const handleResetClick = () => {
        setWarningOpen(true);
    };

    const handleConfirmReset = () => {
        const initialPct = {};
        const initialLowHigh = {};
        sensorColumns.forEach(sensorObj => {
            initialPct[sensorObj.name] = 25.0;
            initialLowHigh[sensorObj.name] = { low: 0, high: 100 };
        });
        setPctThresholds(initialPct);
        setLowHighThresholds(initialLowHigh);
        setWarningOpen(false);
    };

    const sortedSensorColumns = [...sensorColumns].sort((a, b) => {
        const nameA = a.name || '';
        const nameB = b.name || '';
        const numA = parseInt(nameA.replace('IW', ''), 10);
        const numB = parseInt(nameB.replace('IW', ''), 10);
        if (isNaN(numA) || isNaN(numB)) {
            return nameA.localeCompare(nameB);
        }
        return numA - numB;
    });

    return (
        <>
            <div className={styles.modalOverlay} onClick={onClose}>
                <div
                    className={`${styles.modalContent} ${styles.thresholdModalContent}`}
                    onClick={(e) => e.stopPropagation()}
                    data-tut="threshold-modal"
                >
                    <div className={styles.modalHeader}>
                        <h2>Schwellenwerte konfigurieren für {activeFile?.name}</h2>
                        <button
                            onClick={onClose}
                            className={styles.closeButton}
                            data-tut="threshold-close"
                            type="button"
                        >×</button>
                    </div>

                    <div className={styles.tabContainer}>
                        <button
                            className={`${styles.tabButton} ${activeTab === 'percentage' ? styles.activeTab : ''}`}
                            onClick={() => setActiveTab('percentage')}
                            data-tut="threshold-tab-pct"
                            type="button"
                        >Prozentsatz (%)</button>
                        <button
                            className={`${styles.tabButton} ${activeTab === 'lowhigh' ? styles.activeTab : ''}`}
                            onClick={() => setActiveTab('lowhigh')}
                            data-tut="threshold-tab-lowhigh"
                            type="button"
                        >Niedrig / Hoch</button>
                    </div>

                    <div className={styles.modalBody}>
                        <table className={styles.meansTable} data-tut="threshold-table">
                            <thead>
                                <tr>
                                    <th>Sensor</th>
                                    {activeTab === 'percentage' ? (
                                        <th>Schwellenwert (%)</th>
                                    ) : (
                                        <>
                                            <th>Niedriger Wert</th>
                                            <th>Hoher Wert</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedSensorColumns.map(sensorObj => {
                                    const translated = sensorTranslations[sensorObj.name] || 'Benutzerdefinierter Name';
                                    const isEditing = editingSensor === sensorObj.name;

                                    return (
                                        <tr key={sensorObj.name}>
                                            <td className={styles.sensorCell}>
                                                {isEditing ? (
                                                    <div className={styles.sensorEditRow}>
                                                        <div className={styles.sensorLabelGroup}>
                                                            <span className={styles.sensorCode}>{sensorObj.name}</span>
                                                        </div>
                                                        <input
                                                            type="text"
                                                            value={tempName}
                                                            onChange={(e) => setTempName(e.target.value)}
                                                            className={styles.renameInput}
                                                            autoFocus
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') saveName(sensorObj.name);
                                                                if (e.key === 'Escape') cancelEditing();
                                                            }}
                                                        />
                                                        <button type="button" onClick={() => saveName(sensorObj.name)} title="Speichern" className={styles.iconAction}><CheckIcon /></button>
                                                        <button type="button" onClick={cancelEditing} title="Abbrechen" className={styles.iconAction}><CancelIcon /></button>
                                                    </div>
                                                ) : (
                                                    <div className={styles.sensorDisplay}>
                                                        <div className={styles.sensorLabelGroup}>
                                                            <span className={styles.sensorCode}>{sensorObj.name}</span>
                                                            <span className={styles.sensorReadable}>{translated}</span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => startEditing(sensorObj.name, translated)}
                                                            title="Name ändern"
                                                            className={styles.editBtn}
                                                        >
                                                            <EditIcon />
                                                        </button>
                                                    </div>
                                                )}
                                            </td>

                                            {activeTab === 'percentage' ? (
                                                <td>
                                                    <input type="number" value={pctThresholds[sensorObj.name] ?? ''} onChange={(e) => handlePctChange(sensorObj.name, e.target.value)} className={styles.tableInput} />
                                                </td>
                                            ) : (
                                                <>
                                                    <td>
                                                        <input type="number" placeholder="Niedrig" value={lowHighThresholds[sensorObj.name]?.low ?? ''} onChange={(e) => handleLowHighChange(sensorObj.name, 'low', e.target.value)} className={styles.tableInput} />
                                                    </td>
                                                    <td>
                                                        <input type="number" placeholder="Hoch" value={lowHighThresholds[sensorObj.name]?.high ?? ''} onChange={(e) => handleLowHighChange(sensorObj.name, 'high', e.target.value)} className={styles.tableInput} />
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className={styles.modalFooter}>
                        <button type="button" onClick={handleResetClick} className={styles.resetButton} data-tut="threshold-reset">Auf Standardwerte zurücksetzen</button>
                        <button type="button" onClick={onClose} className={styles.cancelButton}>Abbrechen</button>
                        <button type="button" onClick={handleSave} className={styles.saveButton} data-tut="threshold-save">Speichern und Analysieren</button>
                    </div>
                </div>
            </div>

            <WarningModal
                isOpen={isWarningOpen}
                onClose={() => setWarningOpen(false)}
                onConfirm={handleConfirmReset}
                title="Zurücksetzen bestätigen"
            >
                Sind Sie sicher, dass Sie alle Schwellenwerte auf die Standardwerte zurücksetzen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
            </WarningModal>
        </>
    );
}

export default ThresholdModal;
